import React, { useState } from 'react'
import { Button, Stack, Typography, useTheme } from '@mui/material'
import JelledModal from 'components/JelledModal'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { authenticatedUserAtom, organizationMembersAtom, toastAtom } from 'state'
import { InvitationNew, OrgUserRole, OrganizationKind, User } from 'api/model'
import { getColorForDarkMode, isEmailAddressValid, isFieldEmpty } from 'utils'
import { orgsAddMember } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import RoleRadioGroup from './RoleRadioGroup'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { useFetchActiveOrganization } from 'hooks/use-organization'
import { TextFieldWithDarkMode } from 'dark-mode-components'
import { COLORS } from 'app-constants'

const AddUserForm = ({
  organizationId,
  onClose,
}: {
  organizationId?: string | null
  onClose: (user?: User) => void
}) => {
  const theme = useTheme()
  const user = useRecoilValue(authenticatedUserAtom)
  const isMobile = useCheckMobileScreen()

  const setToast = useSetRecoilState(toastAtom)

  const organizationMembers = useRecoilValue(organizationMembersAtom)
  const setOrganizationMembers = useSetRecoilState(organizationMembersAtom)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitCount, setSubmitCount] = useState(0)

  const [name, setName] = useState<string>('')
  const [email, setEmail] = useState<string>('')

  const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
  }

  const [invitationComment, setInvitationComment] = useState<string>('')
  const onInvitationCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInvitationComment(event.target.value)
  }

  const [role, setRole] = useState<OrgUserRole>('member')
  const onRoleChange = (role: OrgUserRole) => {
    setRole(role)
  }

  const userAlreadyExists = organizationMembers?.find((member) => member.email === email)

  const onAddMember = async (isMovingEmployee: boolean) => {
    if (!organizationId) {
      return
    }

    let invitationNew: InvitationNew = {
      name,
      email,
      role,
      invitation_comment: invitationComment,
    }

    try {
      const response = await orgsAddMember(organizationId, invitationNew)
      setOrganizationMembers((members) => [...(members ?? []), response.data])
      const toastTitle = isMovingEmployee ? 'User has been moved successfully' : 'New user has been added'
      setToast({ title: toastTitle, severity: 'success' })
      onClose(response.data)
    } catch {
      const toastTitle = isMovingEmployee ? 'Failed to move user' : 'Add user failed'
      setToast({ title: toastTitle, severity: 'error' })
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderEmailHelperText = () => {
    if (submitCount > 0) {
      if (!isEmailAddressValid(email)) {
        return 'You must enter a valid email'
      } else if (userAlreadyExists) {
        return 'This user already exists'
      }
    } else {
      return (
        <>
          <span style={{ color: COLORS.ORANGE }}>*</span> Invite email will be sent to the email address above
        </>
      )
    }
  }

  const updateEmail = (email: string) => {
    setEmail(email)
  }

  const onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateEmail(event.target.value)
  }

  const onFormSubmit = async () => {
    setSubmitCount((submitCount) => submitCount + 1)

    if (!organizationId || isFieldEmpty(name) || !isEmailAddressValid(email) || userAlreadyExists) {
      return
    }

    setIsSubmitting(true)

    // TODO: the logic here doesn't fully work since we removed plans context
    try {
      await onAddMember(false)
    } catch (error) {
      setToast({ title: 'Add user failed', severity: 'error' })
    }
  }

  return (
    <Stack>
      <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? 2 : 0}>
        <Stack spacing={4} width={isMobile ? '100%' : '480px'} mr={6}>
          <TextFieldWithDarkMode
            className="has-label"
            isDarkBackdrop={false}
            required
            theme={theme}
            label="Full name"
            placeholder="Enter name"
            value={name}
            onChange={onNameChange}
            disabled={isSubmitting}
            error={submitCount > 0 && isFieldEmpty(name)}
            helperText={submitCount > 0 && isFieldEmpty(name) && "You must enter user's name"}
            sx={{
              input: {
                color: 'secondary.main',
              },
              '& ::placeholder': {
                color: 'secondary.main',
              },
            }}
          />

          <TextFieldWithDarkMode
            required
            isDarkBackdrop={false}
            className="has-label"
            theme={theme}
            label="Email"
            placeholder="Add email address"
            value={email}
            onChange={onEmailChange}
            disabled={isSubmitting}
            helperText={renderEmailHelperText()}
            error={submitCount > 0 && (!isEmailAddressValid(email) || !!userAlreadyExists)}
            sx={{
              input: {
                color: 'secondary.main',
              },
              '& ::placeholder': {
                color: 'secondary.main',
              },
            }}
          />

          <TextFieldWithDarkMode
            isDarkBackdrop={false}
            className="has-label"
            theme={theme}
            multiline={true}
            rows={3}
            label="Additional comment"
            placeholder="Add comment"
            value={invitationComment}
            onChange={onInvitationCommentChange}
            disabled={isSubmitting}
            sx={{
              input: {
                color: 'secondary.main',
              },
              '& label.MuiFormLabel-root': {
                color: getColorForDarkMode(theme),
              },
              '& ::placeholder': {
                color: 'secondary.main',
              },
            }}
          />
        </Stack>

        {user?.is_system_admin && (
          <Stack width={isMobile ? '100%' : '400px'}>
            <Stack width={isMobile ? '100%' : '400px'}>
              <Typography variant="body1W" mb={2} color="secondary">
                App access
              </Typography>
              <RoleRadioGroup role={role} onRoleChange={onRoleChange} disabled={false} />
            </Stack>
          </Stack>
        )}
      </Stack>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2} mt={4}>
        <Button
          size="medium"
          onClick={() => onClose()}
          sx={{
            color: getColorForDarkMode(theme),
          }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="medium"
          onClick={onFormSubmit}
          sx={{
            background: COLORS.JELLED_PRIMARY_BLUE,
          }}>
          Save
        </Button>
      </Stack>
    </Stack>
  )
}

const AddUserModal = ({
  organizationId,
  onClose,
}: {
  organizationId?: string | null
  onClose: (user?: User) => void
}) => {
  const isMobile = useCheckMobileScreen()

  const { organization } = useFetchActiveOrganization()
  const organizationKind = organization?.organization_kind

  const getContent = () => {
    return <AddUserForm organizationId={organizationId} onClose={onClose} />
  }

  const getInviteHeaderText = () => {
    if (organizationKind === OrganizationKind.company) {
      return 'Invite new user to your company'
    } else if (organizationKind === OrganizationKind.personal) {
      return 'Invite new user to your personal space'
    }
    return 'Invite new user'
  }

  return (
    <JelledModal
      onClose={onClose}
      isOpen={true}
      modalStyle={{
        backgroundColor: COLORS.PURE_WHITE,
        marginLeft: isMobile ? '16px' : 0,
        marginRight: isMobile ? '16px' : 0,
      }}>
      <Typography mb={4} variant="h4" color="secondary">
        {getInviteHeaderText()}
      </Typography>

      {getContent()}
    </JelledModal>
  )
}

export default AddUserModal
